import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPayoutSecurityConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [notification, setNotification] = useState(null);

  useEffect(() => { fetchConfigs(); }, []);

  const showNotif = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchConfigs = async () => {
    try {
      const res = await axios.get('/api/admin/payout-security-config', { withCredentials: true });
      if (res.data.success) {
        const data = res.data.data || [];
        setConfigs(data);
        const vals = {};
        data.forEach(c => { vals[c.configKey] = c.configValue; });
        setEditValues(vals);
      }
    } catch (e) {
      console.error('Failed to fetch payout security config:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.put(`/api/admin/payout-security-config/${key}`,
        { value: editValues[key] },
        { withCredentials: true });
      showNotif(`${key} updated successfully`);
      fetchConfigs();
    } catch (e) {
      showNotif('Failed to save: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const getFieldType = (key) => {
    if (key.includes('ENABLED')) return 'boolean';
    if (key.includes('MINUTES') || key.includes('ATTEMPTS')) return 'number';
    return 'text';
  };

  const getDescription = (key) => {
    const descriptions = {
      PAYOUT_SESSION_TIMEOUT_MINUTES: 'Session timeout in minutes for payout users',
      PAYOUT_MAX_LOGIN_ATTEMPTS: 'Maximum failed login attempts before lockout',
      PAYOUT_IP_WHITELIST_ENABLED: 'Enable IP whitelist restriction for payout access'
    };
    return descriptions[key] || '';
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">Loading config...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      {notification && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '12px 20px', borderRadius: '8px', fontWeight: '600',
          background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          {notification.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>🔒 Payout Security Config</h2>
        <button onClick={() => window.location.href = '/admin'}
          style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          ← Back to Dashboard
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {configs.map(config => {
          const fieldType = getFieldType(config.configKey);
          const desc = getDescription(config.configKey);
          return (
            <div key={config.configKey} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#333', fontFamily: 'monospace' }}>
                    {config.configKey}
                  </div>
                  {desc && <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{desc}</div>}
                </div>
                <span style={{ fontSize: '11px', color: '#767676' }}>
                  Updated: {config.updatedAt ? new Date(config.updatedAt).toLocaleString() : '-'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {fieldType === 'boolean' ? (
                  <select
                    id={`config-${config.configKey}`}
                    aria-label={`Value for ${config.configKey}`}
                    value={editValues[config.configKey] || 'false'}
                    onChange={e => setEditValues(prev => ({ ...prev, [config.configKey]: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', flex: 1 }}>
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                ) : (
                  <input
                    id={`config-${config.configKey}`}
                    aria-label={`Value for ${config.configKey}`}
                    type={fieldType === 'number' ? 'number' : 'text'}
                    value={editValues[config.configKey] || ''}
                    onChange={e => setEditValues(prev => ({ ...prev, [config.configKey]: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', flex: 1 }}
                    min={fieldType === 'number' ? 1 : undefined}
                  />
                )}
                <button
                  onClick={() => saveConfig(config.configKey)}
                  disabled={saving[config.configKey]}
                  style={{ padding: '8px 20px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {saving[config.configKey] ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {configs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#767676' }}>
          No payout security config found.
        </div>
      )}
    </div>
  );
};

export default AdminPayoutSecurityConfig;
